import { Column } from "primereact/column";
import { protocolsApi } from "../../services/protocols/protocolsApi";
import { Skeleton } from "primereact/skeleton";
import { DataTable } from "../../components/data/DataTable";
import { Button } from "primereact/button";
import Error from "../../components/common/Error";
import { useSelector } from "react-redux";
import { Toolbar } from "primereact/toolbar";
import { useEffect, useRef, useState, useMemo } from "react";
import { useAppDispatch } from "../../hooks/redux";
import { setContextSidebar } from "../../services/base/layoutSlice";
import { Link, useNavigate } from "react-router-dom";
import { getProtocolsFilters } from "../../services/protocols/protocolsSlice";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { ConfirmPopup, confirmPopup } from "primereact/confirmpopup";
import { Dialog } from "primereact/dialog";
import GenerateProtocolsForm from "./GenerateProtocolsForm";

const getInitialLazyParams = () => {
  const defaultParams = {
    first: 0,
    rows: 50,
    page: 0,
    sortField: null,
    sortOrder: null,
    filters: {
      protocolNumber: { value: null, matchMode: "contains" },
      subjectName: { value: null, matchMode: "contains" },
      specialityName: { value: null, matchMode: "contains" },
      teacherName: { value: null, matchMode: "contains" },
      semester: { value: null, matchMode: "contains" },
      issueDate: { value: null, matchMode: "contains" },
      examDate: { value: null, matchMode: "contains" },
      examTypeName: { value: null, matchMode: "contains" },
      academicYearName: { value: null, matchMode: "contains" },
      currentStatus: { value: null, matchMode: "contains" },
    },
  };

  try {
    const savedState = sessionStorage.getItem("dt-protocols-list");
    if (savedState) {
      const parsed = JSON.parse(savedState);
      return {
        first: parsed.first ?? defaultParams.first,
        rows: parsed.rows ?? defaultParams.rows,
        page: parsed.page ?? defaultParams.page,
        sortField: parsed.sortField ?? defaultParams.sortField,
        sortOrder: parsed.sortOrder ?? defaultParams.sortOrder,
        filters: parsed.filters ? { ...defaultParams.filters, ...parsed.filters } : defaultParams.filters,
      };
    }
  } catch (error) {
    console.error("Failed to restore DataTable state:", error);
  }

  return defaultParams;
};

const ProtocolsList = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const dt = useRef<any>(null);
  const [loading, setLoading] = useState(false);
  const toast = useRef<Toast>(null);
  const [deleteProtocol] = protocolsApi.useDeleteProtocolMutation();
  const [copyProtocol] = protocolsApi.useCopyProtocolMutation();
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [lazyParams, setLazyParams] = useState<any>(getInitialLazyParams());
  const isRestoringRef = useRef(true);

  const protocolsFilters = useSelector(getProtocolsFilters);

  const filter = useMemo(
    () => ({
      byFaculty: protocolsFilters.faculties.map((x) => ({
        id: x.facultyId,
      })),
      byTeacher: protocolsFilters.teachers.map((x) => ({
        id: x.teacherId!,
      })),
      bySpeciality: protocolsFilters.specialities.map((x) => ({
        id: x.specialityId,
      })),
      byAcademicYear: protocolsFilters.academicYears.map((x) => ({
        id: x.academicYearId,
      })),
      byDegree: protocolsFilters.degrees.map((x) => ({
        id: x.degreeId,
      })),
      byEducType: protocolsFilters.eduForms.map((x) => ({
        id: x.specialityModeId,
      })),
      byExamType: protocolsFilters.examTypes.map((x) => ({
        id: x.examTypeId,
      })),
    }),
    [protocolsFilters],
  );

  const {
    data: protocolsPage,
    error,
    isLoading,
    isFetching,
    refetch,
  } = protocolsApi.useGetProtocolsQuery({ ...filter, ...lazyParams }, { skip: !isInitialized });

  useEffect(() => {
    dispatch(setContextSidebar(true));
    setIsInitialized(true);
    const timer = setTimeout(() => {
      isRestoringRef.current = false;
    }, 100);
    return () => {
      dispatch(setContextSidebar(false));
      clearTimeout(timer);
    };
  }, [dispatch]);

  const handleRefresh = () => {
    refetch();
  };

  const handleDelete = async (protocolId: number) => {
    setLoading(true);
    try {
      const response = await deleteProtocol(protocolId).unwrap();
      if (response.data === true) {
        toast.current?.show({ severity: "success", summary: "Изтриване", detail: "Протоколът е изтрит." });
      } else {
        toast.current?.show({
          severity: "error",
          summary: "Грешка",
          detail: "Възникна грешка по време на изтриването",
        });
      }
    } catch (error) {
      toast.current?.show({ severity: "error", summary: "Грешка", detail: "Възникна грешка по време на изтриването" });
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (protocolId: number) => {
    setLoading(true);
    try {
      const response = await copyProtocol(protocolId).unwrap();
      if (response.data?.result) {
        toast.current?.show({ severity: "success", summary: "Копиране", detail: "Протоколът е копиран успешно." });
        navigate(`/protocols/${response.data.result}`);
      } else {
        toast.current?.show({
          severity: "error",
          summary: "Грешка",
          detail: response.data?.message || "Възникна грешка по време на копирането",
        });
      }
    } catch (error) {
      toast.current?.show({ severity: "error", summary: "Грешка", detail: "Възникна грешка по време на копирането" });
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = (event: React.MouseEvent<HTMLElement>, protocolId: number) => {
    confirmPopup({
      target: event.currentTarget as HTMLElement,
      message: "Сигурни ли сте, че искате да изтриете този запис?",
      icon: "pi pi-info-circle",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      acceptLabel: "Да",
      rejectLabel: "Не",
      accept: () => handleDelete(protocolId),
    });
  };

  const confirmCopy = (event: React.MouseEvent<HTMLElement>, protocolId: number) => {
    confirmPopup({
      target: event.currentTarget as HTMLElement,
      message: "Сигурни ли сте, че искате да копирате този протокол?",
      icon: "pi pi-info-circle",
      defaultFocus: "reject",
      acceptLabel: "Да",
      rejectLabel: "Не",
      accept: () => handleCopy(protocolId),
    });
  };

  const topButtons = (
    <div className="flex flex-row gap-2">
      <Link to="/protocols/create">
        <Button label="Нов протокол" icon="pi pi-plus" severity="success" />
      </Link>
      <Link to="/protocols/createRecognitionProtocol">
        <Button label="Нов протокол за признаване на оценки" icon="pi pi-plus" severity="success" />
      </Link>
      <Button
        label="Генериране от план"
        icon="pi pi-file"
        severity="info"
        onClick={() => setShowGenerateDialog(true)}
      />
      <Button label="Обновяване" icon="pi pi-refresh" severity="info" onClick={handleRefresh} />
    </div>
  );

  if (isLoading || !isInitialized) return <Skeleton className="w-full h-2rem" />;
  if (error) return <Error error={error} />;

  return (
    <div className="sticky top-0">
      <h1 className="mb-4 mt-0 text-4xl font-light">Протоколи</h1>
      <Toolbar start={topButtons} />
      <DataTable
        ref={dt}
        value={(protocolsPage?.data as any)?.data ?? []}
        emptyMessage="Не бяха открити протоколи."
        lazy
        dataKey="protocolId"
        paginator
        rows={lazyParams.rows}
        rowsPerPageOptions={[10, 25, 50, 100]}
        totalRecords={(protocolsPage?.data as any)?.totalRecords ?? 0}
        loading={isFetching}
        onPage={(e: any) => {
          if (isRestoringRef.current) {
            const newParams = { ...lazyParams, first: e.first, rows: e.rows, page: e.page };
            if (
              newParams.first !== lazyParams.first ||
              newParams.rows !== lazyParams.rows ||
              newParams.page !== lazyParams.page
            ) {
              setLazyParams(newParams);
            }
          } else {
            setLazyParams((prev: any) => ({ ...prev, first: e.first, rows: e.rows, page: e.page }));
          }
        }}
        onSort={(e: any) => {
          if (isRestoringRef.current) {
            const newParams = { ...lazyParams, sortField: e.sortField, sortOrder: e.sortOrder };
            if (newParams.sortField !== lazyParams.sortField || newParams.sortOrder !== lazyParams.sortOrder) {
              setLazyParams(newParams);
            }
          } else {
            setLazyParams((prev: any) => ({ ...prev, sortField: e.sortField, sortOrder: e.sortOrder }));
          }
        }}
        onFilter={(e: any) => {
          if (isRestoringRef.current) {
            const newParams = { ...lazyParams, first: 0, filters: e.filters };
            if (JSON.stringify(newParams.filters) !== JSON.stringify(lazyParams.filters)) {
              setLazyParams(newParams);
            }
          } else {
            setLazyParams((prev: any) => ({ ...prev, first: 0, filters: e.filters }));
          }
        }}
        sortField={lazyParams.sortField}
        sortOrder={lazyParams.sortOrder}
        first={lazyParams.first}
        filters={lazyParams.filters}
        filterDisplay="row"
        className="p-datatable-sm"
        stateStorage="session"
        stateKey="dt-protocols-list"
      >
        <Column field="protocolNumber" header="Номер" sortable filter></Column>
        <Column field="subjectName" header="Дисциплина" sortable filter></Column>
        <Column field="specialityName" header="Специалност" sortable filter></Column>
        <Column field="teacherName" header="Преподавател" sortable filter></Column>
        <Column field="semester" header="Семестър" sortable filter></Column>
        <Column field="issueDate" header="Дата на издаване" sortable filter></Column>
        <Column field="examDate" header="Дата на изпит" sortable filter></Column>
        <Column field="examTypeName" header="Тип изпит" sortable filter></Column>
        <Column
          header="Семестър / Сесия / Година"
          sortable
          sortField="academicYearName"
          filter
          filterField="academicYearName"
          body={(rowData) => (
            <span>
              {rowData.semesterTypeName} / {rowData.sessionTypeName} / {rowData.academicYearName}
            </span>
          )}
        />
        <Column
          field="currentStatus"
          header="Статус"
          sortable
          filter
          body={(rowData) => (
            <Tag
              value={rowData.currentStatus ?? "Неподписан"}
              severity={rowData.currentStatus === "SIGNED" ? "success" : "info"}
            />
          )}
        />
        <Column
          body={(rowData) => (
            <div className="flex gap-2">
              <Link to={`/protocols/${rowData.protocolId}`}>
                <Button icon="pi pi-eye" className="p-button-info" />
              </Link>
              <Button
                icon="pi pi-copy"
                className="p-button-secondary"
                onClick={(e) => confirmCopy(e, rowData.protocolId)}
                disabled={loading}
              />
              <Button
                icon="pi pi-trash"
                className="p-button-danger"
                onClick={(e) => confirmDelete(e, rowData.protocolId)}
                disabled={loading}
              />
            </div>
          )}
        />
      </DataTable>

      <Dialog
        header="Генериране на протоколи от учебен план"
        visible={showGenerateDialog}
        onHide={() => setShowGenerateDialog(false)}
        style={{ width: "50vw" }}
        maximizable
      >
        <GenerateProtocolsForm />
      </Dialog>

      <ConfirmPopup />
      <Toast ref={toast} />
    </div>
  );
};

export default ProtocolsList;
